import React from 'react';
import TurnActionControls from './TurnActionControls';
import { AutoActionIntent } from '../../../Core/State/IJoinedRoomState';

interface AutoActionOptionsComponentProps {
    selectedAutoAction: AutoActionIntent | null;
    onAutoActionChange: (action: string | null, maxCallAmount?: number) => void;
    isEnabled: boolean;
    type?: string;
    potentialActions?: Array<{
        type: string;
        minAmount: number;
        maxAmount: number;
    }> | null;
    smallBlind?: number;
    preActionFoldLabel?: string;
    formatCurrency: (num: number) => string;
    currencyType?: 'fiat' | 'crypto' | 'chips' | 'tournament_chips';
}

const AutoActionOptionsComponent: React.FC<AutoActionOptionsComponentProps> = ({
    selectedAutoAction,
    onAutoActionChange,
    isEnabled,
    type,
    potentialActions,
    smallBlind = 1,
    preActionFoldLabel,
    formatCurrency,
    currencyType = 'fiat'
}) => {
    const autoActionOptions = React.useMemo(() => {
        const actions = potentialActions || [];
        if (type === 'aof') {
            return actions.filter(action => (
                action.type === 'fold' ||
                action.type === 'all-in'
            ));
        }

        const options = actions.filter(action => (
            action.type === 'check' ||
            action.type === 'fold' ||
            action.type === 'call' ||
            action.type === 'all-in'
        ));

        const callAction = actions.find(action => action.type === 'call');
        if (callAction) {
            options.push({
                ...callAction,
                type: 'call-any'
            });
        }

        return options;
    }, [potentialActions, type]);

    if (!isEnabled) return null;

    const handleAutoActionSend = (actionType: string, _amount: number) => {
        const intent = getIntentForAction(actionType, preActionFoldLabel);
        if (!intent) return;

        if (selectedAutoAction === intent) {
            onAutoActionChange(null);
        } else {
            const maxCallAmount = intent === 'call'
                ? autoActionOptions.find(action => action.type === 'call')?.maxAmount
                : undefined;
            if (intent === 'call' && typeof maxCallAmount !== 'number') return;

            onAutoActionChange(actionType, maxCallAmount);
        }
    };

    if ((type === 'texas' || type === 'aof') && autoActionOptions.length > 0) {
        return (
            <TurnActionControls
                gameType={type}
                isSitOut={false}
                isCurrentPlayerTurn={false}
                availableActions={{ actions: autoActionOptions }}
                betAmount={0}
                smallBlind={smallBlind}
                onRejoinClick={() => {}}
                onBetAmountChange={() => {}}
                onSendAction={handleAutoActionSend}
                formatCurrency={formatCurrency}
                currencyType={currencyType}
                isAutoAction={true}
                selectedAutoAction={getSelectedActionForIntent(selectedAutoAction, autoActionOptions, preActionFoldLabel)}
                preActionFoldLabel={preActionFoldLabel}
            />
        );
    }

    return null;
};

const getIntentForAction = (
    actionType: string,
    preActionFoldLabel?: string
): AutoActionIntent | null => {
    switch (actionType) {
        case 'check':
        case 'check-or-fold':
            return 'checkOrFold';
        case 'fold':
            return preActionFoldLabel === 'Check/Fold' ? 'checkOrFold' : 'fold';
        case 'call':
            return 'call';
        case 'call-any':
            return 'callAny';
        case 'all-in':
            return 'allIn';
        default:
            return null;
    }
};

const getSelectedActionForIntent = (
    intent: AutoActionIntent | null,
    actions: Array<{ type: string }>,
    preActionFoldLabel?: string
): string | null => {
    switch (intent) {
        case 'checkOrFold':
            if (actions.some(action => action.type === 'check')) return 'check';
            if (preActionFoldLabel === 'Check/Fold' && actions.some(action => action.type === 'fold')) return 'fold';
            return null;
        case 'fold':
            return actions.some(action => action.type === 'fold') ? 'fold' : null;
        case 'call':
            return actions.some(action => action.type === 'call') ? 'call' : null;
        case 'callAny':
            return actions.some(action => action.type === 'call-any') ? 'call-any' : null;
        case 'allIn':
            return actions.some(action => action.type === 'all-in') ? 'all-in' : null;
        default:
            return null;
    }
};

export default AutoActionOptionsComponent;
